import {
  Avatar,
  Button,
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  ListContext,
  ListContextValue,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { Contributor, ContributorViewResource, DataModelEnum, Permission, PermissionAction } from "generated/openApi";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { ContributorTab } from "./ContributorPage";
import { contributorMatchPageRoute } from "pages/match-main/contributor-matches/ContributorMatchPage/ContributorMatchPage";

export const ContributorPageHeader = (props: {
  rootPermission: Permission;
  crudMode: CrudMode;
  fetch: (id: number) => void;
  reroute: (id: number, crudMode: CrudMode) => void;
  contributor: Contributor;
  setCurrentTab: (value: ContributorTab) => void;
  viewResource: ContributorViewResource;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.contributor.id;

  const save = (contributor: Contributor) => {
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.contributors
        .saveUpdate(contributor)
        .then(() => {
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
          props.reroute(id, "view");
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    } else {
      ApiService.contributors
        .saveCreate(contributor)
        .then((response) => {
          const id = response;
          props.fetch(id);
          props.reroute(id, "view");
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.contributors.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];

  if (isAuthorized(props.rootPermission, PermissionAction.Delete))
    menuItems.push({
      label: <Typography color="error">Delete</Typography>,
      action: () => postDelete(id),
    });

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="md">
        <Avatar string={`${props.contributor.firstName} ${props.contributor.lastName}`} size="large" />

        <Stack>
          <Stack direction="row" spacing="sm" alignItems="center">
            <Typography variant="h6">
              {props.contributor.firstName} {props.contributor.lastName}
            </Typography>
            <Typography fontWeight="light">{props.viewResource.currentTitle}</Typography>
          </Stack>

          <Stack direction="row" spacing="md">
            <Stack direction="row" spacing="sm">
              <Typography variant="caption">Current Partner:</Typography>
              <Typography variant="caption">
                {props.viewResource.currentPartner ? props.viewResource.currentPartner : "None"}
              </Typography>
            </Stack>

            <Stack direction="row" spacing="sm">
              <Typography variant="caption">Current Sub-Partner:</Typography>
              <Typography variant="caption">
                {props.viewResource.currentSubPartner ? props.viewResource.currentSubPartner : "None"}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        <CloseButton confirm={props.unsavedChanges} />
        {props.crudMode === "view" && isAuthorized(props.rootPermission, PermissionAction.Update) && (
          // <UpdateButton onClick={() => props.reroute(id, "edit")} />
          <Button
            onClick={() => {
              props.setCurrentTab("Time & Effort");
              navigate({
                pathname: contributorMatchPageRoute.getPath({
                  id: 0,
                  crudMode: "edit",
                  screenType: "dialog",
                }),
                search: contributorMatchPageRoute.getOptionalParams?.({
                  contributorId: id,
                  hideContributor: true,
                }),
              });
            }}
            variant="outlined"
          >
            + Time & Effort
          </Button>
        )}
        {props.crudMode === "edit" && (
          <SubmitButton onClick={() => save(props.contributor)} disabled={!props.unsavedChanges} />
        )}
      </Stack>
    </Stack>
  );
};
